import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { addCalendarEvent, updateCalendarEvent } from 'api/events';
import { parseEvent } from 'components/Calendar/utils/parseEvents';
import { prepareLocationForApi } from 'utils/location';
import { updateEventStore as updateEventStoreAction } from 'containers/Events/actions';
import { useAction } from 'hooks/useAction';

import EventName from './EventName';
import EventLocation from './EventLocation';
import EventProject from './EventProject';
import EventDate from './EventDate';
import EventTime from './EventTime';
import EventCheckbox from './EventCheckbox';
import EventNotes from './EventNotes';
import { reduceEventDetails } from './reducers/reduceEventClick';

import './style.scss';

dayjs.extend(utc).extend(customParseFormat);

const EventCard = ({
  eventDetails,
  projects,
  myTimeZone,
  selectedDay,
  userDetails,
  onClose,
}) =>  {
  const { displayTz: currentTZValue } = eventDetails?.extendedProps || {};
  const { datePreference, timePreference } = userDetails || {};
  // We want to see if the event props contains displayTz, even without a value so
  // that we know the event card should display the timezone dropdown in the case of
  // a new calendar event that has no value set yet.
  const showTz = Object.prototype.hasOwnProperty.bind(eventDetails?.extendedProps, 'displayTz') || !eventDetails;
  const displayTzValue = showTz ? currentTZValue || myTimeZone : null;

  const [eventTitle, setEventTitle] = useState('');
  const [eventLocation, setEventLocation] = useState({});
  const [eventProjectId, setEventProjectId] = useState(null);
  const [eventStart, setEventStart] = useState(null);
  const [eventEnd, setEventEnd] = useState(null);
  const [displayTz, setDisplayTz] = useState(displayTzValue);
  const [eventAllDay, setEventAllDay] = useState(true);
  const [eventNotes, setEventNotes] = useState('');
  const [isFormValid, setFormValid] = useState(true);
  const [isProjectChanged, setProjectChanged] = useState(false);

  const updateEventStore = useAction(updateEventStoreAction);

  const selectedProject = useMemo(() => {
    return projects.find(project => project.id === parseInt(eventProjectId, 10));
  }, [eventProjectId]);
  const { timezone: projectTimeZone } = selectedProject || {};

  const convertToTimeTuple = (dateStr) => {
    const date = dayjs(dateStr);
    return [
      date.year(),
      date.month() + 1,
      date.date(),
      date.hour(),
      date.minute(),
      date.second(),
      date.day(),
    ];
  };

  const validateForm = () => {
    const isValid = !!eventTitle && !!eventProjectId;
    setFormValid(isValid);
    return isValid;
  };

  const updateEventLocation = (newlocation) => {
    setEventLocation(newlocation);
  };

  const setTimeOnDate = (dateStr, timeStr) => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    const date = dayjs(dateStr);
    return date.hour(hours).minute(minutes).second(seconds ?? 0).format('YYYY-MM-DDTHH:mm');
  };

  const updateEventAllDay = (isAllDay) => {
    if (isAllDay) {
      setEventStart(dayjs(eventStart).format('YYYY-MM-DD'));
      setEventEnd(dayjs(eventEnd).format('YYYY-MM-DD'));
    } else {
      setEventStart(setTimeOnDate(eventStart, '09:00:00'));
      setEventEnd(setTimeOnDate(eventEnd, '10:00:00'));
    }
    setEventAllDay(isAllDay);
  };

  const handleStartChange = (value) => {
    const currentStart = dayjs.tz(eventStart, displayTz);
    const currentEnd = dayjs.tz(eventEnd, displayTz);
    let newStart = dayjs(value, 'YYYY-MM-DD', displayTz);
    // Retain the time of the event.
    if (!eventAllDay) {
      newStart = newStart.hour(currentStart.hour()).minute(currentStart.minute());
    }
    // Keep the start and end in sync as a 1 day event.
    if (dayjs(currentStart).isSame(currentEnd, 'day')) {
      // Need to preserve time of a timeframe event.
      const newEnd = newStart.hour(currentEnd.hour()).minute(currentEnd.minute());
      setEventEnd(newEnd.format('YYYY-MM-DDTHH:mm'));
    }
    // If there's an interval between start and end, adjust
    // end with the same interval if start changes past end.
    else if (newStart.isAfter(currentEnd, 'day')) {
      const diff = currentEnd.diff(currentStart, 'day');
      const newEnd = newStart.add(diff, 'day');
      setEventEnd(newEnd.format('YYYY-MM-DDTHH:mm'));
    }
    setEventStart(newStart.format('YYYY-MM-DDTHH:mm'));
  };

  const handleEndChange = (value) => {
    const currentStart = dayjs.tz(eventStart, displayTz);
    const currentEnd = dayjs.tz(eventEnd, displayTz);
    let newEnd = dayjs(value, 'YYYY-MM-DD', displayTz);
    // Retain the time of the event.
    if (!eventAllDay) {
      newEnd = newEnd.hour(currentEnd.hour()).minute(currentEnd.minute());
    }
    // Maintain current interval if updated end is before the start.
    if (newEnd.isBefore(currentStart)) {
      const diff = currentEnd.diff(currentStart, 'day');
      // Retain time of the event.
      const newStart = newEnd.subtract(diff, 'day')
        .hour(currentStart.hour()).minute(currentStart.minute());
      setEventStart(newStart.format('YYYY-MM-DDTHH:mm'));
    }
    setEventEnd(newEnd.format('YYYY-MM-DDTHH:mm'));
  };

  const handleEventSave = async () => {
    if (!validateForm()) return;

    const preparedLocation = eventLocation ? prepareLocationForApi(eventLocation) : {};

    const eventStartDate = eventAllDay ? dayjs(eventStart).startOf('day').format('YYYY-MM-DD') : eventStart;
    const eventEndDate = eventAllDay ? dayjs(eventEnd).add(1, 'day').startOf('day').format('YYYY-MM-DD') : eventEnd;

    // Calendar reads start/end strings as utc, so if an all day event, we always want the event
    // to fall on the date picked, instead of it being shifted based on timezones. But if it's a
    // timeframe event, then we would want to shift the event on the calendar based on the time
    // e.g. an event on 12/25 at 1am in New York would display on 12/24 if the user is in L.A.
    const startStr = eventAllDay ?
      dayjs.utc(eventStartDate).startOf('day').format('YYYY-MM-DDTHH:mmZ') :
      dayjs.tz(eventStartDate, displayTz).tz(myTimeZone).format('YYYY-MM-DDTHH:mmZ');

    const endStr = eventAllDay ?
      dayjs.utc(eventEndDate).startOf('day').format('YYYY-MM-DDTHH:mmZ') :
      dayjs.tz(eventEndDate, displayTz).tz(myTimeZone).format('YYYY-MM-DDTHH:mmZ');

    if (eventDetails?.id) {
      const data = {
        all_day: eventAllDay,
        display_tz: displayTz,
        end_timetuple: convertToTimeTuple(eventEndDate),
        start_timetuple: convertToTimeTuple(eventStartDate),
        summary: eventTitle,
        wedding_id: eventProjectId,
        event_location: preparedLocation,
        description: eventNotes,
      };
      const responseUpdate = await updateCalendarEvent({
        weddingId: eventDetails?.extendedProps?.weddingId,
        eventId: eventDetails.id,
        eventData: data,
      });

      if (responseUpdate.ok) {
        updateEventStore({
          id: responseUpdate.data.id,
          data: {
            id: responseUpdate.data.id,
            title: eventTitle,
            weddingId: responseUpdate.data.wedding_id,
            allDay: eventAllDay,
            start: startStr,
            end: endStr,
            googleLocation: preparedLocation,
            notes: eventNotes,
            displayTz,
          },
        });
        onClose();
      }
    } else {
      const data = {
        all_day: eventAllDay,
        display_tz: displayTz,
        end_timetuple: convertToTimeTuple(eventEndDate),
        id: null,
        is_new: true,
        start_timetuple: convertToTimeTuple(eventStartDate),
        summary: eventTitle,
        wedding_id: eventProjectId,
        event_location: preparedLocation,
        description: eventNotes,
      };
      const responseAdd = await addCalendarEvent({
        weddingId: eventProjectId,
        eventData: data,
      });

      if (responseAdd.ok) {
        updateEventStore({
          id: responseAdd.data.id,
          data: parseEvent(responseAdd.data),
        });
        onClose();
      }
    }
  };

  const normalizeGoogleLocation = (loc) => {
    if (!loc) return null;

    const {
      region: state,
      formatted_address: formattedAddress,
      ...rest
    } = loc;

    return {
      ...rest,
      state,
      formattedAddress,
    };
  };

  useEffect(() => {
    if (eventDetails) {
      const {
        title = '',
        weddingId,
        startStr: start = '',
        endStr: end = '',
        allDay = true,
        location,
        googleLocation,
        notes,
      } = reduceEventDetails(eventDetails);

      // Support legacy location if google place location doesn't exist.
      const _location = normalizeGoogleLocation(googleLocation) || (location ? { formattedAddress: location } : {});

      let adjustedEnd = end;
      if (allDay) {
        adjustedEnd = end ? dayjs(end).subtract(1, 'day') : start;
      }
      setEventTitle(title);
      setEventProjectId(weddingId);
      setEventStart(dayjs(start).tz(displayTz).format('YYYY-MM-DDTHH:mm'));
      setEventEnd(dayjs(adjustedEnd).tz(displayTz).format('YYYY-MM-DDTHH:mm'));
      setEventAllDay(allDay);
      setEventLocation(_location);
      setEventNotes(notes || '');
      return;
    }
    const selectedDayDayjs = dayjs(new Date(selectedDay)).utc().format('YYYY-MM-DDTHH:mm');
    setEventStart(selectedDayDayjs);
    setEventEnd(selectedDayDayjs);
  }, []);

  useEffect(() => {
    // manual user change
    if (isProjectChanged && !isFormValid) {
      validateForm();
    }
  }, [eventProjectId, isProjectChanged]);

  return (
    <div className="event-card">
      <EventName
        title={eventTitle}
        setTitle={setEventTitle}
        isValid={isFormValid}
        onBlur={() => {
          if (!isFormValid) {
            validateForm();
          }
        }}
      />

      <EventLocation
        location={eventLocation}
        setLocation={updateEventLocation}
      />

      <EventProject
        projectId={eventProjectId}
        setProjectId={setEventProjectId}
        isValid={isFormValid}
        projects={projects}
        setProjectChanged={setProjectChanged}
      />

      <EventDate
        start={eventStart}
        end={eventEnd}
        datePreference={datePreference}
        myTimeZone={myTimeZone}
        projectTimeZone={projectTimeZone}
        displayTz={displayTz}
        setStart={handleStartChange}
        setEnd={handleEndChange}
        setTimeZone={(e) => setDisplayTz(e.target.value)}
      />

      {!eventAllDay && (
        <EventTime
          eventStart={eventStart}
          setEventStart={setEventStart}
          eventEnd={eventEnd}
          setEventEnd={setEventEnd}
          setTimeOnDate={setTimeOnDate}
          timeFormat={timePreference}
          showTz={showTz}
        />
      )}

      <EventCheckbox
        isChecked={eventAllDay}
        setEventAllDay={updateEventAllDay}
      />

      <EventNotes
        notes={eventNotes}
        setNotes={setEventNotes}
      />

      {!isFormValid && (
        <div className="event-card__error-message">
          Please complete all required fields above
        </div>
      )}

      <div className="event-card__edit-buttons">
        <button onClick={onClose}
          className="button button--cancel"
        >
          Cancel
        </button>
        <button
          className="button"
          onClick={() => handleEventSave()}
          disabled={!isFormValid}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default EventCard;
